import { useState, useEffect } from "react";
import { en } from "@/i18n/en";
import { hi } from "@/i18n/hi";
import { mr } from "@/i18n/mr";
import { te } from "@/i18n/te";
import type { Lang, Translations } from "@/i18n/translations";
import { LanguageContext } from "@/contexts/LanguageContext";

const translations: Record<Lang, Translations> = {
  en,
  hi,
  mr,
  te,
};

const getInitialLanguage = (): Lang => {
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const queryLang = params.get("lang");
    if (queryLang && ["en", "hi", "mr", "te"].includes(queryLang)) {
      localStorage.setItem("autonxt-lang", queryLang);
      return queryLang as Lang;
    }
  }
  return (localStorage.getItem("autonxt-lang") as Lang) || "en";
};

const storedLang = getInitialLanguage();

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(storedLang);
  const [t, setT] = useState<Translations>(translations[storedLang] || en);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }

    const handleUrlChange = () => {
      if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        const queryLang = params.get("lang") as Lang;
        if (queryLang && ["en", "hi", "mr", "te"].includes(queryLang) && queryLang !== lang) {
          localStorage.setItem("autonxt-lang", queryLang);
          setLangState(queryLang);
          setT(translations[queryLang] || en);
        }
      }
    };

    window.addEventListener("popstate", handleUrlChange);
    return () => window.removeEventListener("popstate", handleUrlChange);
  }, [lang]);

  const setLang = (newLang: Lang) => {
    localStorage.setItem("autonxt-lang", newLang);
    setLangState(newLang);
    setT(translations[newLang] || en);
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}


